import { useState, useEffect, useContext } from "react";
import { Layout, theme, Button, message, Input, Modal } from "antd";
import { UserOutlined, UserAddOutlined } from "@ant-design/icons";
import LoadingOverlay from "react-loading-overlay-ts";

import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import { AddUserModal } from "../../components/ModalForm";
import { EditUserModal } from "../../components/ModalForm";
import { UserTable } from "../../components/Table";

import { AuthContext } from "../../context/AuthContext";
import { TourContext } from "../../context/TourContext";

import {
  getCompanyUsersApi,
  addUserApi,
  readdUserApi,
  editUserApi,
  removeUserApi,
  searchUsersApi,
} from "../../services/User/UserApis";

const { Content } = Layout;
const { Search } = Input;

const UsersPage: React.FC = () => {
  const { currentGroup, currentUser } = useContext(AuthContext);
  const isSuperAdmin = currentUser?.Role?.Name === "Super Admin";
  const { refAddUser, markPageAsVisited, pages, setOpen, setPageLoaded } =
    useContext(TourContext);

  const [isActive, setActive] = useState(true);
  const [users, setUsers] = useState<any>([]);
  const [user, setUser] = useState<any>({});
  const [filtered, setFiltered] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<string>(
    () => sessionStorage.getItem("usersSearch") || ""
  );
  const [show, setShow] = useState(false);
  const [update, setUpdate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false); // State for confirmation modal
  const [userToDelete, setUserToDelete] = useState<string>(""); // State for confirmation modal
  const [key, setKey] = useState<string>(""); // State for confirmation modal

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const showModal = () => {
    setShow(!show);
    setUpdate(false);
  };

  const showEditModal = (data: any) => {
    const formData = {
      id: data.id,
      Name: data.Name,
      Surname: data.Surname,
      Cellphone: data.Cellphone,
      Email: data.Email,
      Group: data.Group.map((item: any) => item.id),
      Role: data.RoleID,
    };

    setUser(formData);
    setShow(!show);
    setUpdate(true);
  };

  const handleAdd = async (data: any) => {
    setActive(true);
    return await addUserApi(data)
      .then((res) => {
        setUsers([...users, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleResend = async (id: string) => {
    return await readdUserApi(id)
      .then((res) => {
        message.success("Successfully Sent");
        return res;
      })
      .catch((err) => {
        message.error(err);
        console.log(err);
      });
  };

  const handleUpdate = async (id: string, data: any) => {
    setActive(true);
    return await editUserApi(id, data)
      .then((res) => {
        const uppdatedUsers = users.map((user: any) => {
          if (id === user.id) {
            return { ...user, ...res.data };
          }
          return user;
        });

        setActive(false);
        setUsers(uppdatedUsers);
        return res;
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  const handleDelete = (id: string, key: string) => {
    setKey(key);
    setConfirmDelete(true);
    setUserToDelete(id);
  };

  const onSearch = async (e: any) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value) sessionStorage.setItem("usersSearch", value);
    else sessionStorage.removeItem("usersSearch");
    if (value !== "") {
      const result = await searchUsersApi(String(currentGroup), value);
      const groupUserIds = new Set(users.map((u: any) => u.id));
      setFiltered(result.data.filter((u: any) => groupUserIds.has(u.id)));
    } else {
      setFiltered([]);
    }
  };

  const onCancelDelete = () => {
    setConfirmDelete(false);
    setUserToDelete(""); // Clear the group id to delete
  };

  const onConfirmDelete = () => {
    setConfirmDelete(false);
    setActive(true);

    removeUserApi(userToDelete)
      .then((res) => {
        getCompanyUsersApi(currentGroup)
          .then((res) => {
            setActive(false);
            setUsers(res.data);
          })
          .catch((err) => {
            setActive(false);
            console.log(err);
          });
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  useEffect(() => {
    getCompanyUsersApi(currentGroup)
      .then((res) => {
        setActive(false);
        setUsers(res.data);
        setPageLoaded(true);
        setOpen(true);
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  }, [currentGroup]);

  useEffect(() => {
    if (pages.AddMember) {
      markPageAsVisited("AddUser");
    }
  }, []);

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <SideBar />
        <Layout className="site-layout">
          <TopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <UserOutlined
                  style={{ paddingRight: "5px", color: "#f04f05" }}
                />
                Users
              </h2>
              <div className="interactions" ref={refAddUser}>
                <Button onClick={showModal} size="large">
                  <UserAddOutlined style={{ paddingRight: "5px" }} />
                  Add User
                </Button>

                <div>
                  <Search
                    placeholder="Search"
                    size="large"
                    value={searchValue}
                    onChange={(e) => onSearch(e)}
                    style={{ width: "100%", margin: "24px 0" }}
                  />
                </div>

                {update ? (
                  <EditUserModal
                    show={show}
                    user={user}
                    showModal={showModal}
                    handleUpdate={handleUpdate}
                  />
                ) : (
                  <AddUserModal
                    show={show}
                    user={currentUser}
                    showModal={showModal}
                    handleAdd={handleAdd}
                  />
                )}
              </div>

              <UserTable
                users={users}
                filtered={filtered}
                showEditModal={showEditModal}
                handleResend={handleResend}
                handleDelete={handleDelete}
                isSuperAdmin={isSuperAdmin}
              />
            </div>
          </Content>
        </Layout>
      </Layout>

      {key === "1" ? (
        <Modal
          title="Confirm Remove"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move this User inactive?
        </Modal>
      ) : (
        <Modal
          title="Confirm Revert"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move this User active?
        </Modal>
      )}

      {key === "3" && (
        <Modal
          title="Confirm Remove"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move permanently remove this User?
        </Modal>
      )}
    </LoadingOverlay>
  );
};

export default UsersPage;
